// Dialogs (02-building-blocks.md): the native modal dialog, so the page behind can't be reached.
// The focus moves in and comes back; Tab cycles inside; Escape closes the top-most dialog only;
// a press outside closes it and its click doesn't reach the page; required steps ignore both.
import { useEffect, useId, useLayoutEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Button } from './Button';
import { Field } from './Field';
import { useAction } from './Wait';
import type { Context } from '../core/errors';
import { t } from '../i18n';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

/** When the last press began: a dialog takes no click from a press that began before it opened. */
let pressed = -Infinity;
addEventListener('pointerdown', () => void (pressed = performance.now()), { capture: true });

export interface DialogProps {
  title?: string;
  /** The accessible name when there is no title. */
  label?: string;
  alert?: boolean;
  /** A step that can't be cancelled: Escape and outside presses do nothing. */
  required?: boolean;
  /** Focus the first field or button instead of the dialog box. */
  focusFirst?: boolean;
  onClose: () => void;
  className?: string;
  children: ReactNode;
}

/** `hooks`: test hooks on the dialog itself (90), e.g. `data-desyr-invite-sheet`. */
export function Dialog({ title, label, alert, required, focusFirst, onClose, className, children, ...hooks }: DialogProps & Record<`data-${string}`, string>) {
  const ref = useRef<HTMLDialogElement>(null);
  // Where the focus was as it opened, read at the first render: a field's autofocus moves it before any effect.
  const [before] = useState(() => document.activeElement as HTMLElement | null);
  const titleId = useId();
  const close = useRef(onClose);
  close.current = onClose;
  const opened = useRef(0);

  useLayoutEffect(() => {
    opened.current = performance.now();
    const dialog = ref.current!;
    if (!dialog.open) dialog.showModal?.();
    // Everything under it is inert, said as such, the rail and the tab bar too (20, Finding 3, v1.58.48).
    const under: Element[] = [];
    for (let at: Element = dialog; at.parentElement && at !== document.body; at = at.parentElement)
      for (const other of at.parentElement.children) if (other !== at && !other.hasAttribute('inert') && other.tagName !== 'SCRIPT') (other.setAttribute('inert', ''), under.push(other));
    const auto = dialog.querySelector<HTMLElement>('[autofocus]');
    (auto ?? (focusFirst ? dialog.querySelector<HTMLElement>(FOCUSABLE) : null) ?? dialog).focus();
    return () => {
      for (const other of under) other.removeAttribute('inert');
      dialog.close?.();
      // Back to the opener; when it has gone, to the section's heading (20, 07, v1.58.48).
      if (before?.isConnected && before !== document.body) return before.focus?.();
      const heading = document.querySelector<HTMLElement>('.tab-page.on :is(h1, h2), main.page :is(h1, h2)');
      if (heading) (heading.tabIndex = -1), heading.focus({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    const dialog = ref.current!;
    const onCancel = (e: Event) => {
      e.preventDefault();
      if (!required) close.current();
    };
    // A press on the backdrop (outside the box): close, and swallow the click that follows.
    const onPointerDown = (e: PointerEvent) => {
      if (required || e.target !== dialog) return;
      const r = dialog.getBoundingClientRect();
      if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) return;
      const swallow = (c: Event) => {
        c.stopPropagation();
        c.preventDefault();
      };
      addEventListener('click', swallow, { capture: true, once: true });
      // Only this press's own click, which on touch comes after the pointerup, hit where the finger was
      // (none comes when the press removed what it was on): the next press is the person's.
      const release = () => (removeEventListener('click', swallow, { capture: true }), removeEventListener('pointerdown', release, { capture: true }));
      addEventListener('pointerdown', release, { capture: true });
      setTimeout(release, 500);
      close.current();
    };
    dialog.addEventListener('cancel', onCancel);
    dialog.addEventListener('pointerdown', onPointerDown);
    return () => {
      dialog.removeEventListener('cancel', onCancel);
      dialog.removeEventListener('pointerdown', onPointerDown);
    };
  }, [required]);

  const trapTab = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const items = [...ref.current!.querySelectorAll<HTMLElement>(FOCUSABLE)];
    if (!items.length) return e.preventDefault();
    const [first, last] = [items[0], items[items.length - 1]];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === ref.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <dialog
      ref={ref}
      className={className ? `dialog ${className}` : 'dialog'}
      role={alert ? 'alertdialog' : 'dialog'} // said, not left to <dialog>'s own (20, v1.58.46)
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-label={title ? undefined : label}
      tabIndex={-1}
      {...hooks}
      onKeyDown={trapTab}
      // A tap that began before it opened (it came up under the finger) presses nothing in it; keys do.
      onClickCapture={(e) => e.detail > 0 && pressed < opened.current && (e.preventDefault(), e.stopPropagation())}
    >
      {title && (
        <h2 id={titleId} className="dialog-title">
          {title}
        </h2>
      )}
      {children}
    </dialog>
  );
}

/**
 * The open modal dialog on top, if any. A modal dialog makes everything outside it inert, so a popup
 * opened over it (the person card, a toast) is placed inside it, in the top layer above it.
 */
export const topModal = (): HTMLElement | undefined => [...document.querySelectorAll<HTMLDialogElement>('dialog')].filter((d) => d.matches(':modal')).at(-1);

/** topModal() while `shown`, checked again as windows open and close (a toast, the update notice). */
export function useTopModal(shown: boolean): HTMLElement | undefined {
  const [, moved] = useState(0);
  const layer = shown ? topModal() : undefined;
  useEffect(() => {
    if (!shown) return;
    const check = setInterval(() => topModal() !== layer && moved((n) => n + 1), 200);
    return () => clearInterval(check);
  }, [shown, layer]);
  return layer;
}

/** The error line under a dialog's content, announced. */
export const ErrorLine = ({ children }: { children?: ReactNode }) =>
  children ? <p className="error-line" role="alert">{children}</p> : null;

export const DialogButtons = ({ children }: { children: ReactNode }) => <div className="dialog-buttons">{children}</div>;

export interface ConfirmProps {
  title: string;
  text: ReactNode;
  action: string;
  /** "Leaving…"; default "…". */
  busyText?: string;
  destructive?: boolean;
  /** Deleting something important: this text must be typed first (case and outer spaces ignored). */
  confirmText?: string;
  /** Put before the failure's words, e.g. "Couldn't leave. ". */
  errorPrefix?: string;
  context?: Context;
  /** The owner's 400 px confirmations. */
  narrow?: boolean;
  onConfirm: () => Promise<unknown>;
  onClose: () => void;
}

export function Confirm({ title, text, action, busyText = '…', destructive, confirmText, errorPrefix = '', context, narrow, onConfirm, onClose }: ConfirmProps) {
  const { run, busy, message } = useAction(onConfirm, context);
  const [typed, setTyped] = useState('');
  const matches = !confirmText || typed.trim().toLowerCase() === confirmText.trim().toLowerCase();
  return (
    <Dialog title={title} alert focusFirst={narrow} className={narrow ? 'narrow' : undefined} onClose={onClose}>
      <div className="dialog-text">{text}</div>
      {confirmText && (
        <Field label={t('field.typeToConfirm')} placeholder={confirmText} value={typed} onChange={(e) => setTyped(e.target.value)} />
      )}
      <ErrorLine>{message && errorPrefix + message}</ErrorLine>
      <DialogButtons>
        <Button variant="text" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant={destructive ? 'critical-text' : 'text'}
          disabled={!matches || busy}
          onClick={async () => (await run()) && onClose()}
        >
          {busy ? busyText : action}
        </Button>
      </DialogButtons>
    </Dialog>
  );
}

export interface FormDialogProps extends Omit<DialogProps, 'children'> {
  action: string;
  busyText: string;
  context?: Context;
  /** Resolves when done; the dialog then closes. A thrown failure stays in the error line. */
  onSubmit: () => Promise<unknown>;
  /** The action can't be used yet (an empty required field). */
  submitDisabled?: boolean;
  children: ReactNode;
}

export function FormDialog({ action, busyText, context, onSubmit, submitDisabled, children, ...dialog }: FormDialogProps) {
  const { run, busy, message } = useAction(onSubmit, context);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (await run()) dialog.onClose();
  };
  return (
    <Dialog {...dialog}>
      <form onSubmit={submit}>
        {children}
        <ErrorLine>{message}</ErrorLine>
        <DialogButtons>
          <Button variant="text" onClick={dialog.onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="text" type="submit" disabled={busy || submitDisabled}>
            {busy ? busyText : action}
          </Button>
        </DialogButtons>
      </form>
    </Dialog>
  );
}
